import { useEffect, useState } from "preact/hooks";

/**
 * Tracks connectivity for the persistent offline banner (design-spec-
 * today-card.md §3.6). Combines the browser's `navigator.onLine` signal
 * with an explicit `forceOffline` override the app sets whenever a real
 * request to our own API fails with a network error — a device can
 * report `navigator.onLine === true` while sitting behind a captive
 * portal or a dead barn Wi-Fi hop, so a failed request is a more honest
 * "we can't reach the server" signal than the browser flag alone.
 */
export function useOnlineStatus(): {
  isOffline: boolean;
  reportNetworkFailure: () => void;
  reportNetworkSuccess: () => void;
} {
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [forcedOffline, setForcedOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setBrowserOnline(true);
      setForcedOffline(false);
    };
    const handleOffline = () => setBrowserOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return {
    isOffline: !browserOnline || forcedOffline,
    reportNetworkFailure: () => setForcedOffline(true),
    reportNetworkSuccess: () => setForcedOffline(false),
  };
}
